"use client";

import { useEffect, useRef, useState } from "react";
import { Avatar, I } from "@/components/kamibot-v2/discord";

// ============================================================
// Hero Stage — animated Discord UI miniature
// Sequence: 0 idle → 1 typing /설정 → 2 sent → 3 sidebar room appears
//           → 4 quick menu visible → 5 TTS toggled (link msg) → 6 settings drawer
//           → 7 voice dropdown open → 8 picked → 9 speaking → 10 reset
// ============================================================

const STEPS = [
  "idle",      // 0
  "typing",    // 1: typing /설정
  "sent",      // 2: message sent, kamibot replying
  "room",      // 3: temp voice room appears in sidebar, members join
  "menu",      // 4: quick menu buttons visible (channel rename / TTS / lock)
  "linked",    // 5: TTS button clicked → text<>voice link message
  "settings",  // 6: /설정 modal embedded → drawer slides in on right
  "voices",    // 7: TTS voice dropdown auto-opens, popular voices listed
  "picked",    // 8: 피자공주 picked → confirmation
  "speaking",  // 9: kamibot speaks in voice room (waveform pulse)
  "reset",     // 10: brief reset/fade
] as const;

type StepName = (typeof STEPS)[number];

const STEP_DURATION: Record<StepName, number> = {
  idle: 800,
  typing: 1700,
  sent: 1400,
  room: 1500,
  menu: 1500,
  linked: 1800,
  settings: 1500,
  voices: 4500,
  picked: 1800,
  speaking: 2600,
  reset: 800,
};

const PORTRAIT = "/kamibot-character/kamibot-portrait.png";
const EXTENDED = "/kamibot-character/kamibot-extended.png";

type PopularVoice = { emoji: string; name: string; tag: string };

const POPULAR_VOICES: PopularVoice[] = [
  { emoji: "🐱", name: "야옹이", tag: "신규" },
  { emoji: "🦊", name: "호빈이", tag: "" },
  { emoji: "🧑", name: "민준", tag: "" },
  { emoji: "👧", name: "선희", tag: "" },
  { emoji: "😂", name: "웃음봇 1호", tag: "신규" },
  { emoji: "🍃", name: "민초아", tag: "" },
  { emoji: "🍕", name: "피자공주", tag: "신규" },
];

// ----- Sidebar (left) ---------------------------------------------------

function StageSidebar({ stepIdx }: { stepIdx: number }) {
  const showRoom = stepIdx >= 3;
  const showMembers = stepIdx >= 3;

  return (
    <aside className="hd-sidebar">
      <div className="hd-server">
        <span className="hd-server-name">카미봇 지원 서버</span>
        <I.chevron className="hd-server-chev" />
      </div>

      <div className="hd-side-scroll">
        <div className="hd-cat">
          <span className="hd-cat-chev">▾</span>
          <span>카미봇의 기능</span>
          <span className="hd-cat-plus">+</span>
        </div>
        <ul className="hd-channels">
          <li><span className="hd-ch-hash">#</span>AI채팅</li>
          <li><span className="hd-ch-hash">#</span>이모지-추가하기</li>
          <li><span className="hd-ch-hash">#</span>비밀채팅</li>
          <li><span className="hd-ch-hash">#</span>마인크래프트-채팅</li>
          <li><span className="hd-ch-hash">#</span>자동-번역-translator</li>
          <li className={`is-voice ${stepIdx >= 5 ? "is-active" : ""}`}>
            <I.volume className="hd-ch-ico" />
            TTS
            {stepIdx >= 5 && <span className="hd-ch-link" title="텍스트↔음성 연결됨">🔗</span>}
          </li>
          <li className="is-voice hd-add-voice">
            <I.plusCircle className="hd-ch-ico" />
            새 음성 채널
          </li>
        </ul>

        <div className={`hd-temp-room ${showRoom ? "in" : ""}`}>
          <div className="hd-temp-line">
            <I.volume className="hd-ch-ico" />
            <span className="hd-temp-name">Kamilake 카미님의 채널</span>
            <span className="hd-temp-time">2:53</span>
          </div>
          <div className="hd-temp-sub">채널 설정 잠김</div>
          {showMembers && (
            <ul className="hd-temp-members">
              <li className="m-bot">
                <span className="m-av">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={PORTRAIT} alt="" />
                </span>
                <span className="m-name">Kamibot 카미봇</span>
                <span className="m-mute">🎤</span>
              </li>
              <li>
                <span className="m-av m-user">K</span>
                <span className="m-name">
                  Kamilake 카미 <small>봇(?)</small>
                </span>
                <span className="m-mute">🔇</span>
              </li>
            </ul>
          )}
        </div>

        <div className="hd-cat hd-cat-mt">
          <span className="hd-cat-chev">▾</span>
          <span>카미봇 만들기</span>
        </div>
        <ul className="hd-channels">
          <li><span className="hd-ch-hash">#</span>카미의-기술적인-이야기</li>
        </ul>
      </div>

      <div className="hd-me">
        <span className="m-av m-user m-me">K</span>
        <div className="hd-me-meta">
          <b>Kamilake 카미</b>
          <small>
            <span className="m-online" />음성 채널에 사용
          </small>
        </div>
        <I.mic className="hd-me-ico" />
      </div>
    </aside>
  );
}

// ----- Voice room card (main top) -------------------------------------

function VoiceRoomCard({ visible, speaking }: { visible: boolean; speaking: boolean }) {
  return (
    <div className={`hd-voice-card ${visible ? "in" : ""}`}>
      <div className={`hd-voice-tile hd-voice-tile-bot ${speaking ? "is-speaking" : ""}`}>
        <div className="hd-voice-portrait">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={PORTRAIT} alt="카미봇" />
        </div>
        {speaking && (
          <div className="hd-wave">
            <span /><span /><span /><span /><span />
          </div>
        )}
        <div className="hd-voice-tag">
          <I.muted /> Kamibot 카미봇
          {speaking && <span className="hd-speaking-dot" />}
        </div>
      </div>
      <div className="hd-voice-tile hd-voice-tile-user">
        <div className="hd-voice-portrait hd-voice-portrait-user">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={EXTENDED} alt="" />
        </div>
        <div className="hd-voice-tag">
          <I.muted /> Kamilake 카미
        </div>
      </div>
    </div>
  );
}

// ----- Quick menu buttons (chat embed) --------------------------------

function QuickMenu({
  ttsActive,
  onTtsClick,
}: {
  ttsActive: boolean;
  onTtsClick: (e: React.MouseEvent) => void;
}) {
  return (
    <div className="hd-quick">
      <button className="hd-quick-btn" type="button">
        <I.pencil /> 채널 이름 변경
      </button>
      <button
        className={`hd-quick-btn hd-quick-tts ${ttsActive ? "is-on" : ""}`}
        onClick={onTtsClick}
        type="button"
      >
        <I.volume /> TTS {ttsActive ? "켜짐" : "켜기"}
      </button>
      <button className="hd-quick-btn" type="button">
        <I.lock /> 잠금
      </button>
    </div>
  );
}

// ----- Settings drawer (right, slide-in) ------------------------------

function SettingsDrawer({
  open,
  voicesOpen,
  highlightIdx,
  picked,
  pickedVoice,
}: {
  open: boolean;
  voicesOpen: boolean;
  highlightIdx: number;
  picked: boolean;
  pickedVoice: PopularVoice;
}) {
  return (
    <aside className={`hd-drawer ${open ? "in" : ""}`}>
      <div className="hd-drawer-head">
        <Avatar />
        <div className="hd-drawer-meta">
          <b>
            Kamibot 카미봇 <span className="badge-bot is-verified">앱</span>
          </b>
          <small>오전 9:46</small>
        </div>
      </div>

      <div className="hd-drawer-embed">
        <div className="hd-embed-title">
          <span className="hd-emoji">🌸</span> 카미봇 설정
        </div>
        <div className="hd-embed-desc">모든 설정은 대시보드에 있어요</div>
        <div className="hd-embed-actions">
          <a className="hd-pill"><I.gear /> 설정 폼 <I.arrowOut /></a>
          <a className="hd-pill"><I.gear /> 이 채널 설정 <I.arrowOut /></a>
        </div>

        <div className="hd-vbox">
          <div className="hd-vbox-head">
            <b>TTS 목소리 바꾸기</b>
            <a className="hd-vbox-link"><I.volume /> TTS 관리</a>
          </div>
          <div className="hd-vbox-callout">
            <span className="hd-vbox-emoji">✨</span>
            <div>
              <b>새로운 목소리가 생겼어요!</b>
              <small>5월 한정으로 새로 추가된 목소리를 만나보세요</small>
            </div>
          </div>

          <div
            className={`hd-select hd-select-feature ${voicesOpen ? "is-open" : ""} ${
              picked ? "is-picked" : ""
            }`}
          >
            <span className="hd-select-label">
              {picked ? (
                <>
                  <span>{pickedVoice.emoji}</span>
                  <span>
                    <b>{pickedVoice.name}</b> 선택됨
                  </span>
                </>
              ) : (
                <>
                  <span>✨</span> 새로 추가된 목소리 골라보기
                </>
              )}
            </span>
            <I.chevron className="hd-select-chev" />

            <div className="hd-select-menu">
              {POPULAR_VOICES.map((v, i) => (
                <div
                  key={v.name}
                  className={`hd-voice-row ${highlightIdx === i ? "is-hover" : ""}`}
                >
                  <span className="hd-voice-emoji">{v.emoji}</span>
                  <span className="hd-voice-name">{v.name}</span>
                  {v.tag && <span className="hd-voice-tag-pill">{v.tag}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="hd-select hd-select-muted">
            <span className="hd-select-label">
              <span>🔇</span> 음소거 — 40명
            </span>
            <I.chevron className="hd-select-chev" />
          </div>
          <div className="hd-select hd-select-muted">
            <span className="hd-select-label">TTS 녹음 선택</span>
            <I.chevron className="hd-select-chev" />
          </div>

          <small className="hd-vbox-foot">
            내 설정에 더 많은 목소리와 읽는 닉네임 설정이 있어요
          </small>
        </div>
      </div>

      <div className="hd-drawer-foot">
        ◎ 이 메시지는 본인만 볼 수 있어요 · <a>메시지 닫기</a>
      </div>
    </aside>
  );
}

// ----- Main stage container -------------------------------------------

export function HeroStage() {
  const [stepIdx, setStepIdx] = useState(0);
  const [pausedByUser, setPausedByUser] = useState(false);
  const [voiceHover, setVoiceHover] = useState(0);
  const [typed, setTyped] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-advance through steps
  useEffect(() => {
    if (pausedByUser) return;
    const stepName = STEPS[stepIdx];
    const dur = STEP_DURATION[stepName] || 1500;
    const t = setTimeout(() => {
      setStepIdx((p) => (p + 1) % STEPS.length);
    }, dur);
    return () => clearTimeout(t);
  }, [stepIdx, pausedByUser]);

  // Typewriter for /설정 in step 1
  useEffect(() => {
    const stepName = STEPS[stepIdx];
    if (stepName === "typing") {
      const target = "/설정";
      let i = 0;
      setTyped("");
      const id = setInterval(() => {
        i++;
        setTyped(target.slice(0, i));
        if (i >= target.length) clearInterval(id);
      }, 110);
      return () => clearInterval(id);
    } else if (stepName === "idle") {
      setTyped("");
    }
  }, [stepIdx]);

  // Cycle voice highlight in step 7 — end on 피자공주 (idx 6)
  useEffect(() => {
    const stepName = STEPS[stepIdx];
    if (stepName === "voices") {
      let i = 0;
      setVoiceHover(0);
      const id = setInterval(() => {
        i = (i + 1) % POPULAR_VOICES.length;
        setVoiceHover(i);
      }, 580);
      return () => clearInterval(id);
    }
    if (stepName === "picked" || stepName === "speaking") {
      setVoiceHover(6); // 피자공주 stays highlighted
    }
  }, [stepIdx]);

  // Cleanup the resume timer on unmount
  useEffect(() => {
    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, []);

  // Click handler — advance manually, then resume auto in 6s
  const advance = () => {
    setPausedByUser(true);
    setStepIdx((p) => (p + 1) % STEPS.length);
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => setPausedByUser(false), 6000);
  };

  const stepName = STEPS[stepIdx];
  const showVoiceCard = stepIdx >= 3 && stepIdx < 10;
  const showQuickMenu = stepIdx >= 4 && stepIdx < 10;
  const ttsOn = stepIdx >= 5 && stepIdx < 10;
  const drawerOpen = stepIdx >= 6 && stepIdx < 10;
  const voicesOpen = stepIdx >= 7 && stepIdx < 9;
  const picked = stepIdx >= 8 && stepIdx < 10;
  const speaking = stepIdx === 9;
  const pickedVoice = POPULAR_VOICES[6]; // 피자공주

  return (
    <div
      className={`hd-stage step-${stepName} ${drawerOpen ? "has-drawer" : ""}`}
      ref={containerRef}
      onMouseEnter={() => setPausedByUser(true)}
      onMouseLeave={() => setPausedByUser(false)}
      onClick={advance}
    >
      {/* progress dots */}
      <div className="hd-progress">
        {STEPS.slice(1).map((s, i) => (
          <span key={s} className={`hd-dot ${i + 1 <= stepIdx ? "is-on" : ""}`} />
        ))}
      </div>

      <StageSidebar stepIdx={stepIdx} />

      <div className="hd-main">
        <div className="hd-main-head">
          <I.hash className="hd-main-hash" />
          <span className="hd-main-name">Kamilake 카미님의 채널</span>
          <span className="hd-main-topic">채널 설정 잠김</span>
        </div>

        <div className="hd-main-scroll">
          <VoiceRoomCard visible={showVoiceCard} speaking={speaking} />

          <div className="hd-msg-list">
            <div className="hd-msg">
              <Avatar />
              <div className="hd-msg-body">
                <div className="hd-msg-head">
                  <b>Kamibot 카미봇</b>
                  <span className="badge-bot is-verified">앱</span>
                  <small>오전 9:43</small>
                </div>
                <div className="hd-msg-text">
                  <b>Kamilake 카미님의 채널</b>에 오신 걸 환영합니다!
                  <div className="hd-msg-sub">
                    Kamilake 카미님의 가변 음성 채널.
                    TTS 목소리 변경: <code>/설정</code>
                  </div>
                  {showQuickMenu && (
                    <QuickMenu
                      ttsActive={ttsOn}
                      onTtsClick={(e) => {
                        e.stopPropagation();
                        advance();
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            {ttsOn && (
              <div className="hd-msg hd-msg-link">
                <div className="hd-msg-link-row">
                  <I.volume className="hd-msg-link-ico" />
                  <b>Kamilake 카미님의 채널</b>
                  <span className="hd-msg-link-arrow">→</span>
                  <I.hash />
                  <b>Kamilake 카미님의 채널</b>
                </div>
                <small>
                  유료 <code>/tts 목소리 설정</code>:<b>설정</b>
                </small>
              </div>
            )}

            {drawerOpen && (
              <div className="hd-msg hd-msg-mention">
                <Avatar />
                <div className="hd-msg-body">
                  <div className="hd-msg-head">
                    <b>Kamibot 카미봇</b>
                    <span className="badge-bot is-verified">앱</span>
                    <small>오전 9:46</small>
                  </div>
                  <div className="hd-msg-text">
                    <span className="hd-mention">@Kamilake 카미</span>님이{" "}
                    <span className="hd-channel-pill">#설정(봇)</span>을 사용함
                  </div>
                </div>
              </div>
            )}

            {speaking && (
              <div className="hd-msg hd-msg-spoken">
                <div className="hd-msg-av-user">K</div>
                <div className="hd-msg-body">
                  <div className="hd-msg-head">
                    <b className="hd-name-user">Kamilake 카미</b>
                    <small>오전 9:47</small>
                  </div>
                  <div className="hd-msg-text">
                    오늘 회의 시간 다들 괜찮으신가요?
                    <div className="hd-spoken-tag">
                      <I.volume />
                      <span>
                        {pickedVoice.emoji} {pickedVoice.name} 목소리로 읽는 중
                      </span>
                      <span className="hd-spoken-bars">
                        <span /><span /><span /><span />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="hd-input">
          <I.plus className="hd-input-plus" />
          <div className="hd-input-field">
            {stepName === "typing" || stepName === "idle" ? (
              <>
                <span className="hd-input-text">
                  {typed || (
                    <span className="hd-input-ph">Kamilake 카미님의 채널에 메시지 보내기</span>
                  )}
                </span>
                <span className="hd-input-caret" />
                {typed.startsWith("/") && (
                  <div className="hd-slash-pop">
                    <div className="hd-slash-head">
                      <span>
                        <b>카미봇 명령어</b> · 23개 더
                      </span>
                      <span>↑↓ · enter</span>
                    </div>
                    <ul>
                      <li className="active">
                        <span className="ic"><I.gear /></span>
                        <span>
                          <b>/설정</b>
                          <small>서버·채널·TTS 목소리 설정</small>
                        </span>
                        <span className="key">tab</span>
                      </li>
                      <li>
                        <span className="ic"><I.tts /></span>
                        <span>
                          <b>/tts</b>
                          <small>메시지를 음성으로</small>
                        </span>
                      </li>
                      <li>
                        <span className="ic"><I.globe /></span>
                        <span>
                          <b>/번역</b>
                          <small>108개 언어로 자동 번역</small>
                        </span>
                      </li>
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <span className="hd-input-ph">Kamilake 카미님의 채널에 메시지 보내기</span>
            )}
          </div>
          <I.emoji className="hd-input-ico" />
          <I.gift className="hd-input-ico" />
        </div>
      </div>

      <SettingsDrawer
        open={drawerOpen}
        voicesOpen={voicesOpen}
        highlightIdx={voiceHover}
        picked={picked}
        pickedVoice={pickedVoice}
      />
    </div>
  );
}

export default HeroStage;
